<template>
  <div class="customer-story-page">
    <!-- Hero Section -->
    <div class="hero-wrapper">
      <div class="hero-section-wrapper" v-if="heroContent">
        <HeroSection
          :title="heroContent.title"
          :subtitle="heroContent.subtitle"
          :description="heroContent.description"
          :buttonText="heroContent.buttonText"
          :buttonLink="heroContent.buttonLink"
          :image="heroContent.image"
          :navIndicator="heroContent.navIndicator"
        />
      </div>
    </div>

    <!-- Main Content with Sidebar -->
    <div class="content-wrapper">
      <div class="main-content">
        <!-- Key Engagement Metrics Section -->
        <div
          class="section-wrapper key-metrics-section-wrapper padding-container"
          v-if="keyMetrics"
        >
          <KeyEngagementMetrics :metrics="keyMetrics" />
        </div>

        <!-- Challenge Section -->
        <div
          class="section-wrapper background-grey challenge-section-wrapper padding-container"
          v-if="challengeContent"
        >
          <ChallengeSection
            :title="challengeContent.title"
            :quote="challengeContent.quote"
            :quoteAuthor="challengeContent.quoteAuthor"
            :description="challengeContent.description"
            :roadblocks="challengeContent.roadblocks"
            :quoteBackgroundColor="challengeContent.quoteBackgroundColor"
            :order="challengeContent.order"
            :showQuote="challengeContent.showQuote"
            :showQuoteAuthor="challengeContent.showQuoteAuthor"
          />
        </div>

        <!-- Solution Section -->
        <div
          class="section-wrapper solution-section-wrapper padding-container"
          v-if="solutionContent"
        >
          <SolutionSection
            :title="solutionContent.title"
            :subtitle="solutionContent.subtitle"
            :description="solutionContent.description"
            :benefitTitle="solutionContent.benefitTitle"
            :showBenefitTitle="solutionContent.showBenefitTitle"
            :benefits="solutionContent.benefits"
          />
        </div>

        <!-- Results Section -->
        <div
          class="section-wrapper background-grey results-section-wrapper padding-container"
          v-if="resultsContent"
        >
          <ResultsSection
            :title="resultsContent.title"
            :description="resultsContent.description"
            :outcomes="resultsContent.outcomes"
            :summary="resultsContent.summary"
            :watermark-text="resultsContent.watermarkText"
            :divacs-color="resultsContent.divacsColor"
            :showDescription="resultsContent.showDescription"
          />
        </div>

        <!-- Impact Section -->
        <div
          class="section-wrapper impact-section-wrapper padding-container"
          v-if="impactContent"
        >
          <ImpactSection
            :title="impactContent.title"
            :description="impactContent.description"
            :meterDescription="impactContent.meterDescription"
            :categories="impactContent.categories"
            :showMeterDescription="impactContent.showMeterDescription"
          />
        </div>

        <!-- Talent Testimonial Section -->
        <div
          class="section-wrapper background-grey talent-testimonial-section-wrapper padding-container"
          v-if="talentTestimonialContent"
        >
          <TalentTestimonial
            :title="talentTestimonialContent.title"
            :testimonials="talentTestimonialContent.testimonials"
          />
        </div>

        <!-- Customer Testimonial Section -->
        <div
          class="section-wrapper background-grey customer-testimonial-section-wrapper padding-container"
          v-if="customerTestimonialContent"
        >
          <CustomerTestimonial
            :title="customerTestimonialContent.title"
            :testimonial="customerTestimonialContent.testimonial"
          />
        </div>
      </div>

      <!-- Sticky Table of Contents Sidebar -->
      <TableOfContents v-if="tocSections" :sections="tocSections" />
    </div>
    <!-- More Stories Section -->
    <div
      class="section-wrapper more-stories-section-wrapper padding-container "
      v-if="currentStoryId && stories"
    >
      <MoreStories :current-story-id="currentStoryId" :stories="stories" :showTitle="true" />
    </div>
    <!-- Get in Touch Section -->
    <div class="scheduleNow-section-wrapper ">
      <ScheduleNow backgroundColor="#f9f9f9" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, defineProps } from 'vue';
import HeroSection from './HeroSection.vue';
import TableOfContents from './TableOfContents.vue';
import KeyEngagementMetrics from './KeyEngagementMetrics.vue';
import ChallengeSection from './ChallengeSection.vue';
import SolutionSection from './SolutionSection.vue';
import ResultsSection from './ResultsSection.vue';
import ImpactSection from './ImpactSection.vue';
import TalentTestimonial from './TalentTestimonial.vue';
import CustomerTestimonial from './CustomerTestimonial.vue';
import MoreStories from './MoreStories.vue';
import ScheduleNow from '../../../service/components/GetInTouch.vue';

defineProps({
  heroContent: {
    type: Object,
    required: true,
  },
  tocSections: {
    type: Array,
    required: true,
  },
  keyMetrics: {
    type: Array,
    required: true,
  },
  challengeContent: {
    type: Object,
    required: true,
  },
  solutionContent: {
    type: Object,
    required: true,
  },
  resultsContent: {
    type: Object,
    required: true,
  },
  impactContent: {
    type: Object,
    required: true,
  },
  talentTestimonialContent: {
    type: Object,
    required: false,
  },
  customerTestimonialContent: {
    type: Object,
    required: false,
  },
  currentStoryId: {
    type: String,
    required: true,
  },
  stories: {
    type: Array,
    required: true,
  },
});

onMounted(() => {
  document.getElementById('loaderSection').classList.add('d-none');
});
</script>

<style scoped>
.hero-wrapper {
  width: 100%;
  background-color: #f9f9f9;
}
.customer-story-page {
  position: relative;
}

.content-wrapper {
  display: flex;
  max-width: 1280px;
  margin: 0 auto;
  padding-right: 20px;
  position: relative;
}

.main-content {
  flex: 1;
  padding: 20px;
  /* margin-top: 2rem; */
}

.section-wrapper,
.hero-section-wrapper {
  width: 100%;
  position: relative;
  max-width: 1280px;
  margin: 0 auto;
}

/* Full-width background for all sections inside main-content using ::before */
.background-grey::before {
  content: '';
  position: absolute;
  left: 68%;
  width: 99vw;
  height: 100%;
  transform: translateX(-50%);
  background-color: #f9f9f9;
  z-index: -1;
}

.table-of-contents {
  z-index: 10;
}

@media (max-width: 1260px) {
  .background-grey::before {
    width: 100vw;
    left: 68%;
    right: 0;
    transform: translateX(-50%);
  }
}

@media (max-width: 1100px) {
  .background-grey::before {
    width: 100vw;
    left: 75%;
    right: 0;
  }
}

@media (max-width: 1000px) {
  .background-grey::before {
    width: 100vw;
    left: 82%;
    right: 0;
  }
}

@media (max-width: 800px) {
  .content-wrapper {
    flex-direction: column;
  }

 

  .background-grey::before {
    width: 100vw;
    left: 50%;
    right: 0;
    transform: translateX(-50%);
  }
}
@media screen and (max-width: 1475px) {
  .padding-container {
    padding: 0rem 4rem 0rem 3rem;
  }
  
}
@media screen and (max-width: 1370px) {
  .padding-container {
    padding: 0rem 1rem 0rem 5rem;
  }
}
@media screen and (max-width: 1280px) {
  .padding-container {
    padding: 0rem 4rem 0rem 5rem;
  }
}
@media screen and (max-width: 1100px) {
  .padding-container {
    padding: 0 3rem;
  }
}
@media screen and (max-width: 800px) {
  .padding-container {
    padding: 0rem 3rem;
  }
}

@media screen and (max-width: 730px) {
  
  .padding-container {
    padding: 0rem 2rem;
  }
}
@media screen and (max-width: 490px) {
  .padding-container {
    padding: 0rem 1rem 0rem;
  }
}
</style>
